import { MenuCategory } from './types';

export const getSettingsMenuCategories = (): readonly MenuCategory[] => {
  return settingsMenuCategories;
};

const settingsMenuCategories = [
  {
    title: 'Personal',
    id: 'personal',
    items: [
      {
        id: 'profile',
        title: 'Profile',
        route: '/settings/profile',
        icon: 'user',
        segmentId: 'profile',
        featureFlags: [],
      },
      {
        id: 'preferences',
        title: 'Preferences',
        route: '/settings/preferences',
        icon: 'preferences',
        segmentId: 'preferences',
        featureFlags: [],
      },
      {
        id: 'copilot-trial',
        title: 'Co-pilot trial',
        route: '/settings/copilot-trial',
        icon: 'ai',
        segmentId: 'copilotTrial',
        featureFlags: [{ name: 'agent-copilot-trial-eligibility' }],
      },
    ],
  },
  {
    title: 'General',
    id: 'general',
    items: [
      {
        id: 'organization',
        title: 'Organization',
        route: '/settings/organization',
        icon: 'building',
        accessControl: ['organization.info', { fallbackRole: 'admin' }],
        segmentId: 'organization',
        featureFlags: [],
      },
      {
        id: 'agents',
        title: 'Agents',
        route: '/settings/agents',
        icon: 'headset',
        accessControl: [
          [
            'organization.member',
            'organization.access',
            'organization.workload',
          ],
          { strategy: 'any', fallbackRole: 'teamlead' },
        ],
        segmentId: 'agents',
        featureFlags: [],
      },
      {
        id: 'teams',
        title: 'Teams',
        route: '/settings/teams',
        icon: 'team',
        accessControl: ['organization.team', { fallbackRole: 'teamlead' }],
        segmentId: 'teams',
        featureFlags: [
          { name: 'teams-setting-page-and-assigning-teams-to-queues' },
        ],
      },
      {
        id: 'billing',
        title: 'Billing',
        route: '/settings/billing',
        icon: 'credit-card',
        accessControl: ['organization.billing', { fallbackRole: 'admin' }],
        segmentId: 'billing',
        featureFlags: [],
      },
    ],
  },
  {
    title: 'Channels and flows',
    id: 'channels_and_flows',
    items: [
      {
        id: 'queues',
        title: 'Queues',
        route: '/settings/queues',
        icon: 'users',
        accessControl: ['settings.queue:r', { fallbackRole: 'teamlead' }],
        segmentId: 'queues',
        featureFlags: [],
      },
      {
        id: 'phone',
        title: 'Phone',
        route: '/settings/phone',
        icon: 'phone',
        accessControl: [
          ['settings.integration.channel.edit'],
          { strategy: 'all', fallbackRole: 'systemadmin' },
        ],
        segmentId: 'phone',
        featureFlags: [{ name: 'phone-channel' }],
      },
      {
        id: 'chat',
        title: 'Chat',
        route: '/settings/chat',
        icon: 'chat',
        accessControl: [
          ['settings.integration.channel.edit'],
          { strategy: 'all', fallbackRole: 'systemadmin' },
        ],
        segmentId: 'chat',
        featureFlags: [{ name: 'widget-chat-channel' }],
      },
      {
        id: 'email',
        title: 'Email',
        route: '/settings/email',
        icon: 'mail',
        accessControl: [
          ['settings.integration.channel.edit'],
          { strategy: 'all', fallbackRole: 'systemadmin' },
        ],
        segmentId: 'email',
        featureFlags: [{ name: 'email-channel' }],
      },
      {
        id: 'sms',
        title: 'Texts',
        route: '/settings/sms',
        icon: 'sms',
        accessControl: [
          [
            'settings.integration.channel.setup',
            'settings.integration.channel.edit',
          ],
          { strategy: 'all', fallbackRole: 'admin' },
        ],
        segmentId: 'sms',
        featureFlags: [{ name: 'sms-channel' }],
      },
      {
        id: 'third-party-messaging',
        title: 'Third-party messaging',
        route: '/settings/messaging',
        icon: 'messaging-settings',
        accessControl: [
          [
            'settings.integration.channel.setup',
            'settings.integration.channel.edit',
          ],
          { strategy: 'all', fallbackRole: 'admin' },
        ],
        segmentId: 'messaging',
        featureFlags: [
          { name: 'facebook-messenger-channel' },
          { name: 'instagram-management' },
          { name: 'twitter-channel' },
          { name: 'whatsapp-channel' },
        ],
        featureFlagsStrategy: 'any',
      },
      {
        id: 'messenger',
        title: 'Messenger',
        route: '/settings/dixa-messenger',
        icon: 'messenger',
        accessControl: [
          ['settings.integration.channel.edit'],
          { strategy: 'all', fallbackRole: 'admin' },
        ],
        segmentId: 'messenger',
        featureFlags: [{ name: 'has-messenger' }],
      },
      {
        id: 'chatbots',
        title: 'Chatbots',
        route: '/settings/chatbots',
        icon: 'chat-bot',
        accessControl: [
          ['settings.integration.channel.edit', 'settings.chatbot'],
          { strategy: 'all', fallbackRole: 'admin' },
        ],
        segmentId: 'mim',
        featureFlags: [
          { name: 'mim-chatbot' },
          { name: 'ada-chat-channel' },
          { name: 'digitalgenius-chat-channel' },
          { name: 'kindly-chat-channel' },
        ],
        featureFlagsStrategy: 'any',
      },
      {
        id: 'help-centers',
        title: 'Help Centers',
        route: '/settings/help-centers',
        icon: 'question',
        accessControl: [
          'settings.helpcenter:w',
          { fallbackRole: 'systemadmin' },
        ],
        segmentId: 'knowledge',
        featureFlags: [
          {
            name: 'help-center',
          },
        ],
      },
    ],
  },
  {
    title: 'Manage',
    id: 'manage',
    items: [
      {
        id: 'businesshours',
        title: 'Business hours',
        route: '/settings/businesshours',
        icon: 'clock-three',
        accessControl: [
          'settings.business_hours',
          { fallbackRole: 'systemadmin' },
        ],
        segmentId: 'businesshours',
        featureFlags: [],
      },
      {
        id: 'tags',
        title: 'Tags',
        route: '/settings/tags',
        icon: 'tag',
        accessControl: ['settings.tag', { fallbackRole: 'systemadmin' }],
        segmentId: 'tags',
        featureFlags: [],
      },
      {
        id: 'intent-detection',
        title: 'Intent detection',
        route: '/settings/intent-detection',
        icon: 'lightbulb',
        accessControl: [
          'settings.intent_detection',
          { fallbackRole: 'systemadmin' },
        ],
        segmentId: 'intentDetection',
        featureFlags: [{ name: 'intent-detection' }],
        featureFlagsStrategy: 'any',
      },
      {
        id: 'audio-files',
        title: 'Audio files',
        route: '/settings/audio',
        icon: 'volume-up',
        accessControl: ['settings.audio_file', { fallbackRole: 'systemadmin' }],
        segmentId: 'audioFiles',
        featureFlags: [],
      },
      {
        id: 'custom-attributes',
        title: 'Custom attributes',
        route: '/settings/custom-attributes',
        icon: 'input',
        accessControl: [
          'settings.custom_attribute',
          { fallbackRole: 'systemadmin' },
        ],
        segmentId: 'customAttributes',
        featureFlags: [],
      },
      {
        id: 'custom-forms',
        title: 'Forms',
        route: '/settings/custom-forms',
        icon: 'list-ul',
        accessControl: [
          'settings.custom_attribute',
          { fallbackRole: 'systemadmin' },
        ],
        segmentId: 'customForms',
        featureFlags: [{ name: 'has-custom-forms-feature' }],
      },
      {
        id: 'ai-style-guide',
        title: 'Writing style guide',
        route: '/settings/ai/style-guide',
        icon: 'widget-customize',
        accessControl: [
          'settings.writing_style',
          { fallbackRole: 'systemadmin' },
        ],
        segmentId: 'writingStyleGuide',
        featureFlags: [{ name: 'agent-copilot' }],
      },
      {
        id: 'knowledge',
        title: 'Knowledge management',
        route: '/settings/knowledge',
        icon: 'elevio',
        accessControl: ['settings.knowledge:w', { fallbackRole: 'agent' }],
        segmentId: 'knowledge',
        featureFlags: [{ name: 'knowledge-management-nav' }],
      },
      {
        id: 'templates',
        title: 'Templates',
        route: '/settings/templates',
        icon: 'copy',
        accessControl: ['settings.template', { fallbackRole: 'systemadmin' }],
        segmentId: 'templates',
        featureFlags: [],
      },
      {
        id: 'scheduled-conversations',
        title: 'Scheduled conversations',
        route: '/settings/scheduled-conversations',
        icon: 'clock',
        accessControl: ['conversation.import', { fallbackRole: 'systemadmin' }],
        segmentId: 'scheduledConversations',
        featureFlags: [{ name: 'scheduled-conversations' }],
      },
      {
        id: 'integrations',
        title: 'Integrations',
        route: '/settings/integrations',
        icon: 'plug',
        accessControl: [
          'settings.integration.api',
          { fallbackRole: 'systemadmin' },
        ],
        segmentId: 'integrations',
        featureFlags: [],
      },
      {
        id: 'webhooks',
        title: 'Satisfaction',
        route: '/settings/satisfaction',
        icon: 'smile',
        accessControl: [
          'settings.satisfaction',
          { fallbackRole: 'systemadmin' },
        ],
        segmentId: 'satisfactionChat',
        featureFlags: [
          { name: 'csat-for-email' },
          { name: 'csat-for-widget-chat' },
          { name: 'csat-for-dixa-messenger' },
        ],
        featureFlagsStrategy: 'any',
      },
      {
        id: 'automations',
        title: 'Automations',
        route: '/settings/automations',
        icon: 'merge',
        accessControl: [
          ['settings.flow:r'],
          { fallbackRole: 'admin', strategy: 'all' },
        ],
        segmentId: 'automations',
        featureFlags: [{ name: 'automations' }],
      },
      {
        id: 'banned-users',
        title: 'Banned users',
        route: '/settings/banned-users',
        icon: 'ban',
        accessControl: ['settings.contact_ban', { fallbackRole: 'admin' }],
        segmentId: 'bannedUsers',
        featureFlags: [],
      },
      {
        id: 'export-conversations',
        title: 'Export conversations',
        route: '/settings/export-conversations',
        icon: 'download',
        accessControl: ['conversation.export', { fallbackRole: 'admin' }],
        segmentId: 'exportConversations',
        featureFlags: [{ name: 'export-conversations' }],
      },
    ],
  },
  {
    title: 'About',
    id: 'about',
    items: [
      {
        id: 'about',
        title: 'Versions',
        route: '/settings/about',
        icon: 'dixa',
        segmentId: 'versions',
        featureFlags: [],
      },
    ],
  },
] as const satisfies readonly MenuCategory[];

export const menuCategoryIds = settingsMenuCategories.map(
  (category) => category.id,
);
export type MenuCategoryId = (typeof settingsMenuCategories)[number]['id'];
